import { Button } from '@/renderer/components/common';
import { OrderItemShiftModalProps } from '@/renderer/types/sales';
import { Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const normalizeText = (value: string): string => value.trim().toLowerCase();

export const OrderItemShiftModal: React.FC<OrderItemShiftModalProps> = ({
  isOpen,
  itemName,
  sourceTableId,
  sourceTableName,
  sections,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedTableId(null);
    }
  }, [isOpen]);

  const filteredSections = useMemo(() => {
    const query = normalizeText(searchTerm);

    return sections
      .map((section) => {
        const tables = section.tables.filter((table) => {
          if (!query) return true;

          const tableNameMatch = normalizeText(table.name).includes(query);
          const sectionNameMatch = normalizeText(section.name).includes(query);
          return tableNameMatch || sectionNameMatch;
        });

        return {
          ...section,
          tables,
        };
      })
      .filter((section) => section.tables.length > 0);
  }, [searchTerm, sections]);

  const handleSubmit = async () => {
    if (!selectedTableId || isSubmitting) return;
    await onSubmit({ targetTableId: selectedTableId });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45"
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!isSubmitting) onClose();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isSubmitting) onClose();
        }}
        aria-label="Close item shift modal"
      />

      <div className="relative flex w-full max-w-3xl flex-col overflow-hidden rounded-md bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h3 className="text-base font-semibold text-black">Shift Item</h3>
            <p className="text-xs text-black/70">
              {itemName} from {sourceTableName}
            </p>
          </div>
          <button
            type="button"
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black disabled:opacity-50"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close shift item modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="border-b border-gray-200 p-3">
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search section or table"
              className="h-9 w-full rounded border border-gray-300 pl-9 pr-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
            />
          </div>
        </div>

        <div className="max-h-[55vh] space-y-3 overflow-y-auto p-3">
          {filteredSections.length === 0 ? (
            <div className="rounded border border-gray-200 p-4 text-center text-sm text-black/70">
              No tables found.
            </div>
          ) : (
            filteredSections.map((section) => (
              <div
                key={section.id}
                className="rounded border border-gray-200 bg-white"
              >
                <div className="app-bg border-b border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black">
                  {section.name}
                </div>

                <div className="grid grid-cols-1 gap-2 p-2 md:grid-cols-2">
                  {section.tables.map((table) => {
                    const isSelected = selectedTableId === table.id;
                    const isSourceTable = table.id === sourceTableId;

                    return (
                      <button
                        key={table.id}
                        type="button"
                        disabled={isSourceTable}
                        className={`flex items-center justify-between rounded border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          isSelected
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-300 bg-white text-black hover:border-gray-400'
                        }`}
                        onClick={() => setSelectedTableId(table.id)}
                      >
                        <span className="font-medium">{table.name}</span>
                        {table.has_running_order ? (
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                              isSelected
                                ? 'bg-white/20 text-white'
                                : 'app-bg text-black/80'
                            }`}
                          >
                            Running #{table.bill_number || '-'}
                          </span>
                        ) : (
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                              isSelected
                                ? 'bg-white/20 text-white'
                                : 'bg-gray-100 text-black/70'
                            }`}
                          >
                            Empty
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-gray-200 p-3">
          <Button
            type="button"
            className="h-10 w-full text-xs font-semibold uppercase tracking-wide"
            onClick={handleSubmit}
            disabled={!selectedTableId || isSubmitting}
            isLoading={isSubmitting}
          >
            Shift item
          </Button>
        </div>
      </div>
    </div>
  );
};
